import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { customArrayMerge, environment, getFullLocaleFromConfig } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';
import { orderConfig } from './order/order';
import { paymentMethodsConfig } from './payment';

const tenantId = 'lascanach';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'lascana.ch', appDeeplinkProtocol: 'lchapp' },
    clientId: 'LascanaCh',
    i18n: {
      defaultLocale: 'de-CH',
      locales: ['de-CH', 'fr-CH'],
      languages: ['de', 'fr'],
    },
    tenantId,
    company: {
      name: 'Lascana',
      seoTitleName: 'Lascana.ch',
    },
    timeZone: 'Europe/Zurich',
    headerSlots: {
      localeSwitcher: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: '/cms-images/$1/',
      },
      assetServerHost: 'https://bilder.lascana.ch',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: [
          '/',
          '/storefront',
          '/storefront-app',
          '/fr',
          '/mafo/unsubscribe',
          '/uwg7/unsubscribe',
        ],
        prefixes: [
          '/service-hilfe',
          '/aide-service',
          '/lp',
          '/content',
          '/magazin',
          '/magazine',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    order: {
      ...orderConfig,
      supportedPaths: {
        myAccountAddresses: true,
      },
      seals: {},
      assets: {
        // Lascana CH sells Ackermann's Treue Päckli, and bilder.lascana.ch has no premium assets.
        premium: {
          getPremiumImage: ({ language }: { language: string }) =>
            `https://bilder.ackermann.ch/empiriecom/io/ackermannch/${language}/Header_Service_Vorteile_Treuepaeckli.svg`,
          getPremiumIcon: () =>
            'https://bilder.ackermann.ch/empiriecom/io/ackermannch/de/premiumicon.svg',
        },
        app: {
          getQrCode: () =>
            'https://bilder.lascana.ch/empiriecom/io/lascanach/de/Checkout_QR_Code.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.lascana.ch/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      recommendations: {
        wishlist: {
          similarProducts: {
            isEnabled: false,
            type: 'searchReplace',
          },
        },
      },
      accountInformation: {
        bicAvailable: false,
      },
      bankInformation: {
        companyName: 'Ackermann Vertriebs AG',
        bankName: 'Industriestrasse 19 8112 Otelfingen',
        iban: 'CH30 3000 0001 6059 5151 6',
        bic: 'DRESDEFF760',
      },
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      newsletterSettings: {
        useDOI: true,
        sendAdditionalSubscribe: true,
      },
      contact: {
        dynamicHelp,
      },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|99'],
        fields: [
          {
            type: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|99',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.lascana.ch/service-hilfe/newsletter-abmelden',
          voucherLink: 'https://www.lascana.ch/service-hilfe/vorteile/gutscheine-rabatte/',
        },
        fr: {
          unsubscribeLink:
            'https://www.lascana.ch/fr/aide-service/newsletter/desinscription-de-la-newsletter',
          voucherLink: 'https://www.lascana.ch/fr/aide-service/avantages/bons-rabais/',
        },
      },
      braFinder: {
        de: { searchLink: '/s/CUP+{cupSize}%2C+Grösse+{size}' },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-CH': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialActionTarget: 'https://www.lascana.ch/s/',
        },
        'fr-CH': {
          privacyNoticePath: '/fr/aide-service/qui-sommes-nous/protection-des-donnees',
          potentialActionTarget: 'https://www.lascana.ch/s/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    recommendations: {
      provider: 'empiriecom',
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: {
      enabled: true,
      appUrl: 'https://app.adjust.com/1lcy5jjv_1lzawfao',
    },
    tracking: {
      clarityIdProduction: 'x8xe85bnfk',
      categoryIdHomepage: '161441',
      soastaId: 'MTHZ8-Y7JKV-YMN4A-QNLG2-D66Y8',
      tagmanagerId: 'GTM-NR7TKCS',
      googleSiteVerificationID: ['-KF2ltJciuiR-I1bRrpPYNZwB6h0nZji50jQpYgroRc'],
      msvalidateID: '4067315BE5669CD6C74BDEC6F6AC5D47',
      pinterestRichPinVerificationID: 'add46bfb6e0cf176e43e61b50007f9f6',
      schemaOrgOrganisation: {
        'de-CH': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.lascana.ch/',
          name: 'Lascana',
          alternateName: 'LASCANA',
          description:
            'Lascana ist ein Schweizer Online-Shop, der Bademode, Unterwäsche und Lingerie anbietet. Lascana ist bekannt für seine große Auswahl an hochwertigen Produkten und seinen Fokus auf Weiblichkeit und Stil. Lascana ist *nicht* nur ein reiner Online-Händler, sondern ein Anbieter für ein inspirierendes Einkaufserlebnis rund um Bademode, Unterwäsche und Lingerie in der Schweiz.',
          url: 'https://www.lascana.ch/',
          logo: 'https://www.lascana.ch/assets/logos/lascanach/logo.svg',
          foundingDate: '2002-01-01',
          founder: { '@type': 'Person', name: 'Jens Fehnders', description: 'Gründer von Lascana' },
          location: {
            '@type': 'Place',
            name: 'Otelfingen',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Industriestrasse 19',
              addressLocality: 'Otelfingen',
              addressRegion: 'ZH',
              postalCode: '8112',
              addressCountry: 'CH',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+41-848-85-85-07',
              email: 'service@lascana.ch',
              availableLanguage: ['de'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.lascana.ch/service-hilfe/kontakt',
            },
          ],
          sameAs: [
            'https://www.facebook.com/lascanaCH',
            'https://www.instagram.com/lascanaofficial/',
            'https://de.wikipedia.org/wiki/Lascana',
          ],
          legalName: 'Ackermann Vertriebs AG',
          areaServed: 'CH',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Hamburg',
          slogan: 'Make it your world.',
        },
        'fr-CH': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.lascana.ch/fr/',
          name: 'Lascana',
          alternateName: 'LASCANA',
          description:
            "Lascana est une boutique en ligne suisse proposant des maillots de bain, des sous-vêtements et de la lingerie. Lascana est connue pour son large choix de produits de haute qualité et son accent mis sur la féminité et le style. Lascana n'est *pas* seulement un simple détaillant en ligne, mais un fournisseur d'une expérience d'achat inspirante autour des maillots de bain, des sous-vêtements et de la lingerie en Suisse.",
          url: 'https://www.lascana.ch/fr/',
          logo: 'https://www.lascana.ch/assets/logos/lascanach/logo.svg',
          foundingDate: '2002-01-01',
          founder: {
            '@type': 'Person',
            name: 'Jens Fehnders',
            description: 'Fondateur de Lascana',
          },
          location: {
            '@type': 'Place',
            name: 'Otelfingen',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Industriestrasse 19',
              addressLocality: 'Otelfingen',
              addressRegion: 'ZH',
              postalCode: '8112',
              addressCountry: 'CH',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+41-848-85-85-08',
              email: 'service@lascana.ch',
              availableLanguage: ['de', 'fr'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.lascana.ch/fr/aide-service/contact',
            },
          ],
          sameAs: [
            'https://www.facebook.com/lascanaCH',
            'https://www.instagram.com/lascanaofficial/',
            'https://de.wikipedia.org/wiki/Lascana',
          ],
          legalName: 'Ackermann Vertriebs AG',
          areaServed: 'CH',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Hamburg',
          slogan: 'Make it your world.',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Lascana - Onlineshop für Bademode, Lingerie & Unterwäsche ✓ Aktuelle Trends ✓ Flexible Zahlung ✓ Kauf auf Rechnung » Jetzt bestellen!',
        title: 'Lascana » Onlineshop für Bademode, Lingerie & Unterwäsche',
        canonical: 'https://www.lascana.ch',
        robots: 'noodp,index,follow',
      },
      fr: {
        description:
          'Lascana - Votre boutique en ligne de mode balnéaire, lingerie & sous-vêtements ✓ Tendances actuelles ✓ Paiement flexible ✓ Achat sur compte » Commandez maintenant !',
        title:
          'Lascana » Acheter des mode balnéaire, des sous-vêtements et de la lingerie en ligne',
        canonical: 'https://www.lascana.ch',
        robots: 'noodp,index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      enabled: false,
      tenantId: '1400',
    },
    dynamicYieldTracking: {
      key: '656c2ade1a744c7b932774a6b8c7012ac01aa46358d787f92bfd2058a97870a5',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'ch',
      alternatives: [
        {
          flag: 'de',
          shops: [
            {
              link: 'https://www.lascana.de',
              title: 'lascana.de',
            },
          ],
        },
        {
          flag: 'at',
          shops: [
            {
              link: 'https://www.lascana.at',
            },
          ],
        },
      ],
    },
    priceFormat: 'NoDelimiterCurrencyAtTheEnd',
    speedKit: {
      enabled: true,
      scriptUrl: 'https://ottoaustria.app.baqend.com/v1/speedkit/install.js',
    },
    ...paymentMethodsConfig,
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
